<template>
    <div id="breadcrumbs" class="d-flex px-3 pt-2 me-1">
        <div class="flex-fill d-flex align-items-center">
            <h3 class="text-body-emphasis d-none d-lg-block m-0" v-if="lastCrumb && lastCrumb.node">
                {{ $t(lastCrumb.node.name) }}
            </h3>
            <div class="ms-lg-auto">
                <portal-target name="breadcrumbs" />
                <template v-if="allCrumbs.length > 0 && !breadcrumbStore.isLoading">
                    <template v-for="(entry, index) in allCrumbs">
                        <router-link
                            v-if="entry && entry.node && !entry.isCurrent"
                            :key="`link-${index}`"
                            class="link"
                            :to="getBreadcrumbUrl(entry)"
                        >
                            {{ $t(entry.node.name) }}
                        </router-link>
                        <span
                            v-else-if="entry && entry.node && entry.isCurrent && !entry.route"
                            :key="`span-${index}`"
                            class="link active"
                        >
                            {{ $t(entry.node.name) }}
                        </span>
                        <router-link
                            v-else-if="entry && entry.node && entry.isCurrent && entry.route"
                            :key="`current-link-${index}`"
                            class="link active"
                            :to="getBreadcrumbUrl(entry)"
                        >
                            {{ $t(entry.node.name) }}
                        </router-link>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useSitemap } from '@/plugins/sitemap';
import { useBreadcrumbStore } from '@/store/breadcrumbs';
import { BreadcrumbEntry } from '@/helpers/NavigationHistory';

const route = useRoute();
const { $sitemap } = useSitemap();
const breadcrumbStore = useBreadcrumbStore();

const allCrumbs = computed(() => breadcrumbStore.allCrumbs);

const lastCrumb = computed(() =>
{
    return breadcrumbStore.lastCrumb;
});

const getBreadcrumbUrl = (entry: BreadcrumbEntry): any =>
{
    // Handle case where route is null (extra crumbs)
    if (!entry.route)
    {
        // For extra crumbs, we don't have a specific route, so return current route
        return route.fullPath;
    }

    // For breadcrumb entries with routes, use the route directly
    if (entry.route.name)
    {
        return {
            name: entry.route.name,
            params: entry.route.params || {},
            query: entry.route.query || {}
        };
    }

    // Fallback to sitemap URL generation
    try
    {
        // Ensure the route has required properties
        if (!entry.route.fullPath)
        {
            entry.route.fullPath = route.fullPath;
        }

        return $sitemap.url(entry.node, entry.route);
    }
    catch (error)
    {
        return route.fullPath;
    }
};

// Watch for changes in sitemap extraCrumb
watch(() => $sitemap.extraCrumb, (value: string) =>
{
    breadcrumbStore.setExtraCrumb(value);
});

// Watch for route changes
watch(route, async (newRoute) =>
{
    $sitemap.append('');

    try
    {
        const sitemapNode = await $sitemap.find(newRoute);

        if (sitemapNode)
        {
            await breadcrumbStore.updateBreadcrumbs(newRoute, sitemapNode);
        }
    }
    catch (error)
    {
        // Clear breadcrumbs on error to prevent stale data
        breadcrumbStore.clearHistory();
    }
}, { immediate: true });
</script>

<style lang="scss">
#breadcrumbs {
    height: var(--ideo-breadcrumbs-height);

    .link {
        color: var(--bs-body-color);

        &:not(.separator) {
            cursor: pointer;
        }

        &.active {
            color: var(--bs-primary);
            font-weight: 500;
        }

        &:not(:last-child)::after {
            content: "\f324";
            font-family: var(--fa-font-family);
            font-size: 8px;
            vertical-align: 1px;
            display: inline-block;
            color: var(--bs-gray-400);
            padding: 0 8px;
        }
    }
}
</style>
